/**
 * `<SearchEmptyState>` — the Search surface's idle (no-query) presentation.
 *
 * U7 SEARCH slice (ui-design-review-2026-07-05.md P3 item 8: "Search idle:
 * wire the existing-but-dead SavedSearchesPanel ... one line explaining
 * Hybrid/Lexical/Semantic"). History: the original guided-console version
 * (7 workflow-starter cards + corpus KPI tiles + recent queries) was
 * stripped entirely on 2026-06-01 per Chris's "super clean — just the search
 * bar" ask, which left the idle canvas EMPTY and this component unwired.
 * This version restores only the two quiet affordances the review asked for,
 * preserving the centered-launcher composition:
 *
 *   1. Saved searches — `<SavedSearchesPanel>`, rendered ONLY when the
 *      backend actually has saved searches (the v1 endpoint returns an empty
 *      list — a documented seam — so most installs render nothing here
 *      rather than a lonely ghost card).
 *   2. One quiet mono line explaining the three retrieval modes.
 */

import type { SavedSearch } from '@/types';
import { useSavedSearches } from '@/hooks';
import { SavedSearchesPanel } from './SavedSearchesPanel';

interface SearchEmptyStateProps {
  /** Click-through from a saved-search row — the surface writes the URL state. */
  onJump: (search: SavedSearch) => void;
}

export function SearchEmptyState({ onJump }: SearchEmptyStateProps): JSX.Element {
  // The panel owns its own list render; this wrapper only decides presence.
  // The duplicate hook call is deduped by tanstack-query's cache.
  const savedQuery = useSavedSearches();
  const hasSaved = (savedQuery.data?.items.length ?? 0) > 0;

  return (
    <div className="se-idle">
      {hasSaved && <SavedSearchesPanel onJump={onJump} />}
      <p className="se-idle__modes">
        Hybrid blends semantic + keyword · Lexical is exact words · Semantic is by meaning
      </p>
    </div>
  );
}
